import {
  PLATFORM_STATISTIC,
  SR_STAGE_SCENARIO,
  DEVICE_ALL,
  SDK_VERSION_ALL,
  PERFORMANCE_VARIANT_GROUP,
  SDK_ENABLED_START_MODE,
  SDK_DISABLED_START_MODE,
  BASELINE_DAYS,
  DAY_MS,
  MIN_RANGE_RUNS,
  DISPLAY_EPSILON,
  RELEASE_CHANGE_RATIO,
} from './constants';
import { displayDeviceLabel, cleanAppLabel } from './format';

export const isMissing = (value) => value === null || value === undefined;

function mean(values) {
  const present = values.filter((value) => !isMissing(value));
  return present.length ? present.reduce((total, value) => total + value, 0) / present.length : null;
}

// The usual range of a figure: average ± 2 standard deviations of the earlier
// runs. Fewer than MIN_RANGE_RUNS runs give an average but no range.
export function usualRange(values) {
  const present = values.filter((value) => !isMissing(value) && Number.isFinite(Number(value))).map(Number);
  if (!present.length) return null;
  const average = present.reduce((total, value) => total + value, 0) / present.length;
  if (present.length < MIN_RANGE_RUNS) return { mean: average, count: present.length, low: null, high: null };
  const variance = present.reduce((total, value) => total + (value - average) ** 2, 0) / (present.length - 1);
  const spread = 2 * Math.sqrt(variance);
  return { mean: average, count: present.length, low: average - spread, high: average + spread };
}

export function verdictOf(value, range) {
  if (isMissing(value) || !range || isMissing(range.low)) return null;
  if (value > range.high + DISPLAY_EPSILON) return 'above';
  if (value < range.low - DISPLAY_EPSILON) return 'below';
  return 'steady';
}

// Release against the previous release: 'above'/'below' only for a move of at
// least RELEASE_CHANGE_RATIO and at least the KPI's noise floor.
export function releaseVerdictOf(value, previous, floor) {
  if (isMissing(value) || isMissing(previous)) return null;
  const change = value - previous;
  if (Math.abs(change) < floor || Math.abs(change) < RELEASE_CHANGE_RATIO * Math.abs(previous)) return 'steady';
  return change > 0 ? 'above' : 'below';
}

function runTime(run) {
  return new Date(run?.uploadedAt).getTime();
}

// Runs of the BASELINE_DAYS before a run, the run itself left out.
export function runsBefore(runs, run) {
  const end = runTime(run);
  if (!Number.isFinite(end)) return [];
  return runs.filter((other) => {
    const time = runTime(other);
    return other.id !== run.id && time < end && time >= end - BASELINE_DAYS * DAY_MS;
  });
}

function deviceMatches(metric, selectedDevice) {
  return selectedDevice === DEVICE_ALL || (metric.device || '-') === selectedDevice;
}

export function sdkVersionMatches(metric, selectedSdkVersion) {
  return selectedSdkVersion === SDK_VERSION_ALL || (metric.sdkVersion || '-') === selectedSdkVersion;
}

export function visiblePlatformMetrics(metrics, selectedPlatform, selectedDevice, selectedSdkVersion = SDK_VERSION_ALL) {
  return (metrics || []).filter((metric) => (
    metric.platform === selectedPlatform
    && deviceMatches(metric, selectedDevice)
    && sdkVersionMatches(metric, selectedSdkVersion)
  ));
}

export function runMetrics(metrics, runId, selectedPlatform, selectedDevice, selectedSdkVersion = SDK_VERSION_ALL) {
  return visiblePlatformMetrics(metrics, selectedPlatform, selectedDevice, selectedSdkVersion).filter((metric) => metric.runId === runId);
}

// Most repeats any filtered figure averages.
export function runCountOf(metrics) {
  return (metrics || []).reduce((count, metric) => Math.max(count, Number(metric.replicaCount) || 1), 0);
}

export function runOptions(runCount) {
  return Array.from({ length: runCount > 1 ? runCount : 0 }, (_, index) => String(index + 1));
}

export function deviceOptions(metrics, selectedPlatform) {
  return [...new Set(
    (metrics || [])
      .filter((metric) => metric.platform === selectedPlatform && metric.device)
      .map((metric) => metric.device)
  )].sort((a, b) => displayDeviceLabel(a).localeCompare(displayDeviceLabel(b), undefined, { numeric: true }));
}

function appVersionOptions(metrics, selectedPlatform) {
  return [...new Set(
    (metrics || [])
      .filter((metric) => metric.platform === selectedPlatform && metric.appVersion)
      .map((metric) => metric.appVersion)
  )].sort();
}

export function sdkVersionOptions(metrics, selectedPlatform, selectedDevice) {
  return [...new Set(
    (metrics || [])
      .filter((metric) => metric.platform === selectedPlatform && deviceMatches(metric, selectedDevice) && metric.sdkVersion)
      .map((metric) => metric.sdkVersion)
  )].sort(compareVersions);
}

// Newest first; numeric so "4.10.0" sorts above "4.9.0".
function compareVersions(a, b) {
  return String(b).localeCompare(String(a), undefined, { numeric: true });
}

// Nightly runs test the default builds (label ends in _ipa/_apk); release runs
// test a versioned build, e.g. ios_native_testappSRE2E_ipa4.53.0-rc.1.
export function isReleaseRun(run) {
  return runAppLabels(run).some((label) => !/_(ipa|apk)$/.test(label));
}

function runAppLabels(run) {
  const appLabels = run?.metadata?.appLabels;
  const labels = Array.isArray(appLabels) && appLabels.length ? appLabels : [run?.metadata?.appName];
  return [...new Set(labels.map(cleanAppLabel).filter(Boolean))];
}

export const higher = (value, worst) => value > worst;

const lower = (value, worst) => value < worst;

export const avgOf = (metric) => metric?.avg ?? null;

const valueOf = (metric) => metric?.value ?? null;

const startupOf = (metric) => metric?.median ?? metric?.value ?? null;

const metricValue = (metric, statistic) => (statistic ? metric?.[statistic] ?? null : null);

const metricValueWithFallback = (metric, statistic) => {
  if (!statistic) return null;
  if (statistic === 'avg') return metric?.avg ?? metric?.median ?? metric?.value ?? null;
  return metric?.[statistic] ?? metric?.value ?? null;
};

const scenarioKey = (metric) => metric?.scenario || '-';

// Picks the metric holding the worst reading.
function worstMetric(metrics, read, isWorse) {
  return (metrics || []).reduce((worst, metric) => {
    const value = read(metric);
    if (isMissing(value)) return worst;
    return worst === null || isWorse(value, read(worst)) ? metric : worst;
  }, null);
}

export function worstValue(metrics, read, isWorse) {
  return read(worstMetric(metrics, read, isWorse));
}

export function isSrStageMetric(metric) {
  const required = SR_STAGE_SCENARIO[metric?.platform];
  return !required || metric?.scenario === required;
}

// A Session Replay stage's average per capture; the slowest device when
// several reported it.
export function srStageValue(metrics, name) {
  const stageMetrics = (metrics || []).filter((metric) => metric.name === name && isSrStageMetric(metric));
  return stageMetrics.length ? worstValue(stageMetrics, avgOf, higher) : null;
}

function groupByDevice(metrics) {
  const groups = new Map();
  (metrics || []).forEach((metric) => {
    const device = metric.device || '-';
    groups.set(device, [...(groups.get(device) || []), metric]);
  });
  return [...groups.values()];
}

function scenariosOf(metrics) {
  return [...new Set((metrics || []).map(scenarioKey))];
}

function androidScenarioFigure(metrics, name, scenario) {
  const statistic = PLATFORM_STATISTIC.android[name];
  const findMetric = (group, startMode) => metrics.find((metric) => (
    metric.group === group
    && metric.name === name
    && scenarioKey(metric) === scenario
    && (!startMode || metric.startMode === startMode)
  ));
  // 'performance' holds the SDK-started minus SDK-never-started delta under
  // 'benchmark'; an absolute under 'autostart' must never pass for a cost.
  const overheadMetric = findMetric('performance', 'benchmark');
  const enabledMetric = findMetric(PERFORMANCE_VARIANT_GROUP, SDK_ENABLED_START_MODE);
  const disabledMetric = findMetric(PERFORMANCE_VARIANT_GROUP, SDK_DISABLED_START_MODE);
  const enabled = metricValue(enabledMetric, statistic);
  const disabled = metricValue(disabledMetric, statistic);
  const overhead = metricValue(overheadMetric, statistic) ?? (enabled !== null && disabled !== null ? enabled - disabled : null);
  return {
    overhead,
    enabled,
    disabled,
    scenario,
    device: overheadMetric?.device || enabledMetric?.device || disabledMetric?.device || null,
  };
}

// iOS reports the SDK-started run and the SDK-never-started run separately;
// the cost is their difference. FPS is a cost when it drops, so frames lost is
// the never-started run minus the started one.
function iosScenarioFigure(metrics, name, scenario) {
  const statistic = PLATFORM_STATISTIC.ios[name];
  const findMetric = (group, startMode) => metrics.find((item) => (
    item.group === group
    && item.name === name
    && scenarioKey(item) === scenario
    && (!startMode || item.startMode === startMode)
  ));
  const metric = findMetric('performance') || findMetric(PERFORMANCE_VARIANT_GROUP, SDK_ENABLED_START_MODE);
  const disabledMetric = findMetric(PERFORMANCE_VARIANT_GROUP, SDK_DISABLED_START_MODE);
  const enabled = metricValueWithFallback(metric, statistic);
  const sharedBaseline = Boolean(disabledMetric?.sharedBaseline);
  const disabled = metricValueWithFallback(disabledMetric, statistic);
  let overhead = null;
  if (enabled !== null && disabled !== null) overhead = name === 'fps' ? disabled - enabled : enabled - disabled;
  return {
    overhead,
    enabled,
    disabled,
    scenario,
    sharedBaseline,
    device: metric?.device || disabledMetric?.device || null,
  };
}

// A device's figure comes from the scenario where the SDK costs the most. An
// iOS run without an SDK-never-started baseline has no cost, so its raw
// reading still comes from its worst scenario.
function deviceFigure(deviceMetrics, name, platform) {
  const scenarioFigure = platform === 'android' ? androidScenarioFigure : iosScenarioFigure;
  const candidates = scenariosOf(deviceMetrics).map((scenario) => scenarioFigure(deviceMetrics, name, scenario));
  const withCost = candidates.filter((figure) => !isMissing(figure.overhead));
  if (withCost.length) return withCost.reduce((worst, figure) => (figure.overhead > worst.overhead ? figure : worst));
  if (platform === 'android') return null;
  const withReading = candidates.filter((figure) => !isMissing(figure.enabled));
  if (!withReading.length) return null;
  const isWorse = name === 'fps' ? lower : higher;
  return withReading.reduce((worst, figure) => (isWorse(figure.enabled, worst.enabled) ? figure : worst));
}

// The devices' figures, averaged.
function platformFigure(metrics, name, platform) {
  if (!platform || !PLATFORM_STATISTIC[platform]?.[name]) return null;
  const figures = groupByDevice(metrics)
    .map((deviceMetrics) => deviceFigure(deviceMetrics, name, platform))
    .filter(Boolean);
  if (!figures.length) return null;
  const devices = figures.map((figure) => figure.device).filter(Boolean);
  if (figures.length === 1) return { ...figures[0], devices, deviceCount: 1 };
  return {
    sharedBaseline: figures.some((figure) => figure.sharedBaseline),
    overhead: mean(figures.map((figure) => figure.overhead)),
    enabled: mean(figures.map((figure) => figure.enabled)),
    disabled: mean(figures.map((figure) => figure.disabled)),
    scenario: null,
    device: null,
    devices,
    deviceCount: figures.length,
  };
}

export function summarizeMetrics(metrics) {
  const list = metrics || [];
  const platform = list[0]?.platform;
  const figures = {
    cpu: platformFigure(list, 'cpu', platform),
    total_memory: platformFigure(list, 'total_memory', platform),
    fps: platformFigure(list, 'fps', platform),
  };
  const dataUsage = list.filter((metric) => metric.group === 'data_usage' && metric.unit === 'kb_per_min');
  // Startup is timed once per scenario; each device keeps its slowest.
  const startupByDevice = groupByDevice(list.filter((metric) => metric.group === 'time' && metric.name === 'sdk_initialize'))
    .map((deviceMetrics) => ({ device: deviceMetrics[0].device, value: worstValue(deviceMetrics, startupOf, higher) }))
    .filter((entry) => !isMissing(entry.value));
  const appVersions = appVersionOptions(list, platform);
  return {
    sdkVersion: list.find((metric) => metric.sdkVersion)?.sdkVersion || null,
    appVersion: appVersions.length === 1 ? appVersions[0] : (appVersions.length ? `${appVersions.length} versions` : null),
    devices: deviceOptions(list, platform),
    cpu: figures.cpu?.overhead ?? null,
    memory: figures.total_memory?.overhead ?? null,
    fps: figures.fps?.overhead ?? null,
    figures,
    srDataUsageKbPerMin: worstValue(dataUsage, valueOf, higher),
    srDataUsageDevices: [...new Set(dataUsage.filter((metric) => !isMissing(valueOf(metric))).map((metric) => metric.device).filter(Boolean))],
    sdkInitialize: mean(startupByDevice.map((entry) => entry.value)),
    sdkInitializeDevices: startupByDevice.map((entry) => entry.device).filter(Boolean),
  };
}

// Every scenario of every device with its with-SDK, without-SDK and cost
// figures, per metric of the platform.
export function scenarioFigures(metrics, platform) {
  const names = Object.keys(PLATFORM_STATISTIC[platform] || {});
  const scenarioFigure = platform === 'android' ? androidScenarioFigure : iosScenarioFigure;
  return groupByDevice(metrics)
    .flatMap((deviceMetrics) => scenariosOf(deviceMetrics).map((scenario) => ({
      scenario,
      device: deviceMetrics[0].device || '-',
      figures: Object.fromEntries(names.map((name) => [name, scenarioFigure(deviceMetrics, name, scenario)])),
    })))
    .filter((row) => Object.values(row.figures).some((figure) => !isMissing(figure.overhead) || !isMissing(figure.enabled)))
    .sort((a, b) => a.scenario.localeCompare(b.scenario) || displayDeviceLabel(a.device).localeCompare(displayDeviceLabel(b.device), undefined, { numeric: true }));
}

// Devices behind a card's figure.
export function figureDevices(kpi, summary) {
  if (kpi.metric) return summary?.figures?.[kpi.metric]?.devices || [];
  return kpi.devices(summary);
}

// Two runs' figures compare only when the same devices measured them: a
// device joining or leaving moves an average with no change in the SDK.
export function sameDevices(kpi, summary, other) {
  const devices = [...figureDevices(kpi, summary)].sort();
  const otherDevices = [...figureDevices(kpi, other)].sort();
  return devices.length === otherDevices.length && devices.every((device, index) => device === otherDevices[index]);
}

// Trend runs, oldest first, with each run's summary through the page filters.
export function trendEntries(reportedRuns, summaries) {
  return reportedRuns.slice().reverse().map((run) => ({ run, summary: summaries.get(run.id) }));
}

// The iOS without-SDK app runs once a night, so a single repeat has no
// baseline of its own: it borrows the night's one without-SDK run.
export function sharedIosBaselines(repeatMetrics, allRepeatMetrics) {
  if (!allRepeatMetrics) return [];
  const key = (metric) => JSON.stringify([metric.runId, metric.device, metric.scenario, metric.group, metric.name, metric.startMode]);
  const present = new Set((repeatMetrics || []).map(key));
  return allRepeatMetrics
    .filter((metric) => metric.startMode === SDK_DISABLED_START_MODE && !present.has(key(metric)))
    .map((metric) => ({ ...metric, sharedBaseline: true }));
}
